/**
 * mfe-management Routes
 *
 * Defines all settings section routes:
 * - /preferences - User preferences (theme, notifications, hotkeys, confirmations)
 * - /security - Security settings (2FA, trusted devices, privacy mode)
 * - /profile - User profile information
 * - /payments - Payment methods and ACH linking
 * - /accounts - Account management
 * - /api-access - API key management
 * - /add-ons - Extensions management
 * - /subscriptions - Subscription plans
 *
 * All views are lazy-loaded to reduce initial bundle size.
 * This ensures /welcome and /register routes load fast (Web Vitals optimization).
 */

import React from 'react'
import { Navigate, RouteObject } from 'react-router-dom'

// Lazy-load App layout
const ManagementApp = React.lazy(() => import('./App').then(m => ({ default: m.App })))

// Lazy-load all view components for code splitting
const PreferencesView = React.lazy(() => import('./views/preferences/PreferencesView').then(m => ({ default: m.PreferencesView })))
const SecurityView = React.lazy(() => import('./views/security/SecurityView').then(m => ({ default: m.SecurityView })))
const ProfileView = React.lazy(() => import('./views/profile/ProfileView').then(m => ({ default: m.ProfileView })))
const PaymentsView = React.lazy(() => import('./views/payments/PaymentsView').then(m => ({ default: m.PaymentsView })))
const AccountsView = React.lazy(() => import('./views/accounts/AccountsView').then(m => ({ default: m.AccountsView })))
const ApiAccessView = React.lazy(() => import('./views/apiAccess/ApiAccessView').then(m => ({ default: m.ApiAccessView })))
const AddOnsView = React.lazy(() => import('./views/addOns/AddOnsView').then(m => ({ default: m.AddOnsView })))
const SubscriptionsView = React.lazy(() => import('./views/subscriptions/SubscriptionsView').then(m => ({ default: m.SubscriptionsView })))

export function getManagementRoutes(): RouteObject[] {
  return [
    {
      path: '/settings',
      element: <ManagementApp />,
      handle: {
        sections: [
          { to: 'preferences', label: 'Preferences' },
          { to: 'security', label: 'Security' },
          { to: 'profile', label: 'Profile' },
          { to: 'payments', label: 'Payment Methods' },
          { to: 'accounts', label: 'Accounts' },
          { to: 'api-access', label: 'API Access' },
          { to: 'add-ons', label: 'Add-ons' },
          { to: 'subscriptions', label: 'Subscriptions' },
        ]
      },
      children: [
        {
          index: true,
          element: <Navigate to="preferences" replace />
        },
        {
          path: 'preferences',
          element: <PreferencesView />,
          handle: { title: 'Preferences', description: 'Customize your trading experience' }
        },
        {
          path: 'security',
          element: <SecurityView />,
          handle: { title: 'Security', description: 'Protect your account and data' }
        },
        {
          path: 'profile',
          element: <ProfileView />,
          handle: { title: 'Profile', description: 'Manage your personal information' }
        },
        {
          path: 'payments',
          element: <PaymentsView />,
          handle: { title: 'Payment Methods', description: 'Manage your funding sources for deposits and subscriptions' }
        },
        {
          path: 'accounts',
          element: <AccountsView />,
          handle: { title: 'Accounts', description: 'Manage your trading accounts' }
        },
        {
          path: 'api-access',
          element: <ApiAccessView />,
          handle: { title: 'API Access', description: 'Manage API keys and access tokens' }
        },
        {
          path: 'add-ons',
          element: <AddOnsView />,
          handle: { title: 'Add-ons', description: 'Manage extensions and integrations' }
        },
        {
          path: 'subscriptions',
          element: <SubscriptionsView />,
          handle: { title: 'Subscriptions', description: 'Manage subscription plans' }
        }
      ]
    }
  ]
}

export default getManagementRoutes
